<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { gsap } from 'gsap'
import { SplitText } from 'gsap/SplitText';

const props = defineProps({
    title: String,
    content: {
        type: String,
        required: true,
    },
    image: Array,
    afterImageContent: String,
    points: Array,
})

const bookingGeneratingRef = ref(null)
const titleRef = ref(null)
const contentRef = ref(null)
const imageRef = ref(null)
const afterImageContentRef = ref(null)
let splitInstances = []
let observerRef = null

const scrollBottom = () => {

    if (bookingGeneratingRef.value) {
        gsap.to(bookingGeneratingRef.value, {
            scrollTop: bookingGeneratingRef.value.scrollHeight,
            duration: 2,
            delay: 0.5,
            ease: "power2.out",
        });
    }
}

onMounted(() => {
    const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
            observer.disconnect(); // Empêche de relancer plusieurs fois

            // Populate content refs with HTML first
            if (contentRef.value) {
                contentRef.value.innerHTML = props.content;
            }
            if (afterImageContentRef.value) {
                afterImageContentRef.value.innerHTML = props.afterImageContent;
            }

            // Use nextTick to ensure v-html has updated the DOM
            nextTick(async () => {
                await document.fonts.ready;
                const masterTl = gsap.timeline()

                // Animate main content paragraphs
                if (contentRef.value) {
                    const paragraphs = contentRef.value.querySelectorAll('p');
                    paragraphs.forEach((p) => {
                        const splitParagraph = new SplitText(p, { type: "words" });
                        splitInstances.push(splitParagraph)
                        // Ensure words are initially hidden, then animate to visible
                        masterTl.fromTo(splitParagraph.words, { opacity: 0, filter: "blur(10px)" },
                        {
                            opacity: 1, // Animate to full opacity
                            filter: "blur(0px)", // Animate to no blur
                            stagger: 0.025,
                            duration: 0.5,
                            ease: "power1.out",
                        }, ">0.25"); // Start 0.5s after the end of the previous animation
                    });
                }
                
                masterTl.fromTo(imageRef.value.querySelectorAll('.image-container'), {
                            opacity: 0,
                            scale: 0.95,
                        }, {
                            opacity: 1,
                            scale: 1,
                            stagger: .25,
                            duration: 0.5,
                            onComplete: () => {
                                scrollBottom();
                            }
                        });
                    

                masterTl.fromTo(imageRef.value.querySelectorAll('.image-container img'), {
                            opacity: 0,
                            scale: 0.95,
                        }, {
                            opacity: 1,
                            scale: 1,
                            stagger: .25,
                            duration: 0.5,
                            onComplete: () => {
                                scrollBottom();
                            }
                        }, ">-0.25");
                    


                // Animate after image content paragraphs
                if (afterImageContentRef.value) {
                    const afterParagraphs = afterImageContentRef.value.querySelectorAll('p');
                    afterParagraphs.forEach((p) => {
                        const splitParagraph = new SplitText(p, { type: "words" });
                        splitInstances.push(splitParagraph)
                        masterTl.fromTo(splitParagraph.words,  { opacity: 0, y: 10, filter: "blur(10px)" },{
                            opacity: 1, // Animate to full opacity
                            y: 0, // Animate to original y position
                            filter: "blur(0px)", // Animate to no blur
                            stagger: 0.02,
                            duration: 0.5,
                            ease: "power1.out",
                        }, ">0.5"); // Start 0.5s after the end of the previous animation
                    });
                }
            }); // End of nextTick
        }
    }, {
        threshold: 0.3 // déclenche quand 30% du bloc est visible
    });

    if (bookingGeneratingRef.value) {
        observer.observe(bookingGeneratingRef.value);
    }
    observerRef = observer
});

onBeforeUnmount(() => {
    if (observerRef) observerRef.disconnect()
    for (const s of splitInstances) s.revert()
    splitInstances = []
})
</script>

<template>
    <section>
        <div class="booking-generating" ref="bookingGeneratingRef">
        <div class="title-container">
            <h2 ref="titleRef" class="booking-generating__title">{{ title }}</h2>
            <svg class="spark-large" xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26"
                fill="none">
                <path
                    d="M13 0C13 3.58216 11.5411 6.83868 9.19639 9.19639C6.8517 11.5411 3.59519 13 0 13C3.58216 13 6.83868 14.4589 9.19639 16.8036C11.5411 19.1483 13 22.4048 13 26C13 22.4178 14.4589 19.1613 16.8036 16.8036C19.1483 14.4589 22.4048 13 26 13C18.8226 13 13 7.17735 13 0Z"
                    fill="#45B1FE" />
            </svg>
            <svg class="spark-small" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"
                fill="none">
                <path
                    d="M9 0C9 2.47996 7.98998 4.73447 6.36673 6.36673C4.74349 7.98998 2.48898 9 0 9C2.47996 9 4.73447 10.01 6.36673 11.6333C7.98998 13.2565 9 15.511 9 18C9 15.52 10.01 13.2655 11.6333 11.6333C13.2565 10.01 15.511 9 18 9C13.0311 9 9 4.96894 9 0Z"
                    fill="#217DE6" />
            </svg>
        </div>
        <div ref="contentRef" class="booking-generating__content" v-html="content"></div>
        <div ref="imageRef" class="booking-generating__image-main-container">
            <template v-if="image && image.length > 0" v-for="(img, idx) in image" :key="idx">
                <div class="image-container">
                    <NuxtImg 
                        format="webp" 
                        :src="img" 
                        alt="Booking Generating Image" 
                        class="booking-generating__image"
                    />
                </div>
            </template>

        </div>

        <div v-if="afterImageContent" ref="afterImageContentRef" class="booking-generating__content" v-html="afterImageContent"></div>
    </div>

     <div class="booking-generating__points-main-container">
            <template v-if="points && points.length > 0" v-for="(point, idx) in points" :key="idx">
                <div class="point-container">
                    <h3 class="title">
                        {{ point.title }}
                    </h3>
                    <p class="content" v-html="point.content"></p>
                </div>
            </template>

        </div>
    </section>
    
</template>

<style lang="scss" scoped>
.booking-generating {
    max-width: 1250px;
    border-radius: 25px;
    border: 2px solid #143778;
    background: linear-gradient(203deg, #0b0f17 12.97%, #142736 85.19%);
    box-shadow: 0px 0px 40px 0px rgba(19, 107, 162, 0.5);
    margin: 0 auto;
    padding: 50px 25px;

    &::-webkit-scrollbar-track{
        background: transparent;
        margin-block: 25px;
    }

    &::-webkit-scrollbar{
        width: 24px;
        background-color: transparent;
        right: 5px;
        padding: 10px;
    }

    &::-webkit-scrollbar-thumb{
        border-radius: 15px;
        background-image: linear-gradient(145deg, #1D5990 1.64%, #29A6DF 101.31%);
        border: 8px solid transparent;
        background-clip: padding-box;
    }

    & .title-container {
        position: relative;
        width: fit-content;
        margin: 0 auto 50px;

        & .spark-large {
            position: absolute;
            top: -13px;
            right: -20px;
            width: 26px;
            height: 26px;
        }

        & .spark-small {
            position: absolute;
            top: 0px;
            right: -35px;
            width: 18px;
            height: 18px;
        }
    }

    &__title {
        font-size: 2rem;
        @include animated-background-gradient;
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        width: fit-content;
    }

    &__content {
        margin-bottom: 1rem;
        text-align: justify;

        /* SplitText will create spans/divs, ensure they flow naturally */
        & > div, & > span {
            display: inline-block; /* Allows words to wrap naturally */
            white-space: pre-wrap; /* Preserves spaces between words */
             /* Initially blurred */
        }
    }

    & .booking-generating__image-main-container {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-wrap: wrap;
        gap: 20px;
        margin: 50px 0;

        & .image-container {
            width: 100%;
            max-width: 400px;
            height: 320px;
            border-radius: 20px;
            border: 1px solid #4B799E;
            position: relative;
            opacity: 0;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                opacity: 0.4;
                background: linear-gradient(122deg, #072954 1.27%, #326492 50.98%, #245077 95.27%);
                z-index: 0;
            }

            &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-image: url('/images/image-placeholder.svg');
                background-repeat: no-repeat;
                background-position: center;
                z-index: 1;
            }
        }
    }


    &__image {
       max-width: calc(100% + 2px);
        margin-bottom: 1rem;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        object-fit: cover;
        position: relative;
        border: 1px solid #4B799E;
        z-index: 2;
        object-position: center;
        border-radius: 20px;
        margin: -1px;
    }

    &__after-image-content {
        font-size: 1rem;
        color: #666;
        text-align: center;
    }
}

.booking-generating__points-main-container{
    max-width: 1250px;
    margin: 0 auto;
    padding: 150px 0px 0;
    display: grid;
    grid-template-columns: 1fr;
    flex-direction: column;
    gap: 75px;
    

    & .point-container {
       position: relative;

       &:after {
           content: '';
           position: absolute;
           bottom: -15px;
           left: 0;
           right: 0;
           height: 1px;
           opacity: 0.4;
           background: linear-gradient(90deg, #1B3FF5 0%, #1C86E9 48.56%, #1DCDD3 100%); 
        }

        & .title {
            font-size: 34px;
            color: $white;
            margin-bottom: 25px;
            line-height: 1.2em;
        }

        & .content {
            padding: 0;
        }
    }
}

@media (min-width: 767px) {
    .booking-generating {
        padding: 75px 115px;

        &__points-main-container {
            padding: 150px 100px 0;
            grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));

            & .point-container:after {
                content: unset;
            }
        }
        & .title-container {
            margin-bottom: 30px;
        }
        &__title {
            font-size: 1.5rem;
        }
        &__content {
            font-size: 0.9rem;
        }
    }
}

/* .typing-cursor::after {
  content: '|';
  animation: blink 1s step-start infinite;
  margin-left: 2px;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
} */
</style>
